import { useState } from "react";
import { ArrowLeft, ChefHat, CircleSlash, Minus, Plus, StickyNote, UserCircle, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { fmtQty, sumQty, lineTotal } from "@/lib/qty";
import { inr, MONEY_LINE, MONEY_CAPTION } from "@/lib/format";
import { EmptyState } from "@/components/ui/StateBoundaries";
import { useCaptainAllTablesState } from "@/hooks/useCaptainOperationalState";
import { useCaptainOrder } from "@/hooks/useCaptainOrder";
import { NetworkIndicator } from "@/components/captain/NetworkIndicator";
import { CaptainCancelDialog } from "@/components/captain/CaptainCancelDialog";
import { meshTablesPath, meshCustomerPath, meshMenuPath, meshSummaryPath, meshKotsPath } from "@/lib/captainMesh/routes";
import { kotIsServerAcknowledged, saveOrderMapping } from "@/lib/captainMesh/db";
import { getOrder, cancelOrder as cancelOrderOnServer } from "@/lib/orderApi";
import ItemNoteDialog from "@/components/orders/ItemNoteDialog";
import { useNotePresets } from "@/lib/itemNotes";
import SendWaitingButton from "@/components/captain/SendWaitingButton";

const CANCELLABLE = new Set(["open", "kot_sent"]);

/**
 * One line of the current round — always editable, because only un-fired lines
 * reach this screen at all: × on the left removes the whole line, the − qty +
 * stepper on the right moves one plate at a time. Fired lines (queued or
 * acknowledged) live in View KOT; their way back is the cancel flow, not a
 * silent local edit.
 */
function SummaryLine({ item, label, accentClass, onVoid, onDelta, onNote, busy }) {
  // − steps a whole plate; below 1.5 the next stop would be under the half-
  // portion floor, so the × (remove the line) is the only way down from there.
  const canStepDown = item.quantity - 1 >= 0.5;
  return (
    <div className="flex items-center gap-2 p-3 hover:bg-muted/20 animate-slide-in-right" data-testid="summary-line">
      <button
        className="w-10 h-11 flex items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive shrink-0 transition-colors"
        onClick={onVoid}
        disabled={busy}
        aria-label={`Remove ${item.item_name}`}
        data-testid="line-remove"
      >
        <X className="w-5 h-5"/>
      </button>
      {/* Tapping the line opens its special note (owner, 2026-10-20). */}
      <button
        type="button"
        className="flex-1 min-w-0 flex flex-col text-left min-h-11"
        onClick={onNote}
        disabled={busy}
        aria-label={`Note for ${item.item_name}`}
        data-testid="line-note"
      >
        <strong className="text-body font-semibold truncate text-foreground mb-1 w-full">{item.item_name}</strong>
        <div className="flex items-center justify-between gap-3 w-full">
          <small className={`text-micro font-bold uppercase ${accentClass}`}>{label}</small>
          <span className={`${MONEY_CAPTION} font-bold text-muted-foreground tabular-nums`}>{inr(item.unit_price)}</span>
        </div>
        {item.notes ? (
          <span className="flex items-center gap-1 text-meta text-warning mt-1 w-full" data-testid="line-note-text">
            <StickyNote className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{item.notes}</span>
          </span>
        ) : (
          <span className="text-micro text-muted-foreground mt-1">Tap to add a note</span>
        )}
      </button>
      <div className="flex items-center rounded-lg border bg-card shrink-0 overflow-hidden">
          <button
            className="w-10 h-11 flex items-center justify-center text-foreground hover:bg-muted disabled:opacity-30 transition-colors"
            onClick={() => onDelta(-1)}
            disabled={busy || !canStepDown}
            aria-label={`One less ${item.item_name}`}
            data-testid="line-minus"
          >
            <Minus className="w-4 h-4"/>
          </button>
          <b className="w-9 text-center tabular-nums text-body font-bold text-foreground" data-testid="line-qty">
            {fmtQty(item.quantity)}
          </b>
          <button
            className="w-10 h-11 flex items-center justify-center text-foreground hover:bg-muted transition-colors"
            onClick={() => onDelta(1)}
            disabled={busy}
            aria-label={`One more ${item.item_name}`}
            data-testid="line-plus"
          >
            <Plus className="w-4 h-4"/>
          </button>
        </div>
    </div>
  );
}

export default function CaptainSummary() {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const {
    token, tables, orderBusy,
    cancelOrder, orderError
  } = usePOS();

  const { orderForTable } = useCaptainAllTablesState();

  const table = tables?.find(t => t.id === parseInt(tableId) || t.id === tableId);
  const runningOrder = table ? orderForTable(table.id) : null;
  const { removeItem, updateQuantity, fireKOT, setNote } = useCaptainOrder(runningOrder?.order_id);
  const { presets: notePresets } = useNotePresets(token);

  const [cancelPrompt, setCancelPrompt] = useState(null);
  const [noteFor, setNoteFor] = useState(null);
  const saveNote = async note => {
    const line = noteFor;
    await setNote(line.line_id, note);
    toast.success(note ? `Note saved for ${line.item_name}` : `Note removed from ${line.item_name}`);
    return true;
  };

  const back = () => navigate(meshMenuPath(tableId));

  const customer = runningOrder?.customer;

  // "removed" is the tombstone the reducer writes for a voided line.
  const lines = Object.values(runningOrder?.items || {}).filter(
    item => item.status !== "cancelled" && item.status !== "removed"
  );
  // This screen is the CURRENT ROUND: only lines no KOT intent has named yet.
  // Anything fired — acknowledged or still queued for the server — lives in
  // View KOTs, never here: showing cooked food next to
  // the new punches read as "the old order is still selected". The intent
  // filter (not kot_number) keeps the no-refire rule: a queued line is out of
  // this round even before the server confirms it.
  const pending = lines.filter(item => !item.kot_intent_event_id);

  // Summed in paise: adding rupee floats puts ₹499.99999999 in front of a guest.
  const subtotal =
    Math.round(pending.reduce((paise, item) => paise + lineTotal(item.unit_price, item.quantity) * 100, 0)) / 100;

  const voidLine = async (item) => {
    const result = await removeItem(item.line_id);
    if (!result) return;
    toast.success(`${item.item_name} removed`);
    // Result holds the new event, we can't immediately see the remaining length from it,
    // but the live query will update. We might not need the "last-line" prompt if the
    // order gets voided eventually or we just leave it empty. Let's just remove the item.
  };

  const confirmCancelOrder = async ({ reasonCode, reason, authorizedBy }) => {
    let serverId = runningOrder?.server_order_id;
    if (!serverId && token) {
      try {
        const { listActiveOrders } = await import("@/lib/orderApi");
        const board = await listActiveOrders(token);
        const row = table ? board.find(o => o.table_id === table.id) : null;
        serverId = row?.id ?? null;
      } catch {}
    }
    if (!serverId) {
      try {
        const { markAggregateRejected, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
        if (runningOrder?.order_id) {
          await markAggregateRejected(runningOrder.order_id);
          if (markAggregateDiscarded) await markAggregateDiscarded(runningOrder.order_id);
        }
        setCancelPrompt(null);
        toast.success(`${table?.name || "Order"} cancelled`);
        navigate(meshTablesPath());
        return "done";
      } catch (err) {
        toast.error(`Could not cancel order: ${err?.message || err}`);
        return;
      }
    }
    try {
      const current = await getOrder(serverId, token);
      let result = { void_slips: 0 };
      if (current.status !== "cancelled" && current.status !== "closed") {
        result = await cancelOrderOnServer(
          serverId, { reasonCode, reason, authorizedBy }, current.version, token
        );
      }
      const { saveOrderMapping, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
      await saveOrderMapping(runningOrder.order_id, {
        order_id: serverId,
        order_status: "cancelled",
      });
      if (markAggregateDiscarded) await markAggregateDiscarded(runningOrder.order_id);
      setCancelPrompt(null);
      navigate(meshTablesPath());
      toast.success(
        `Order${runningOrder.order_number ? ` #${runningOrder.order_number}` : ""} cancelled` +
          (result?.void_slips ? ` · ${result.void_slips} VOID slip(s) sent` : "")
      );
      return "done";
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string" && (detail.toLowerCase().includes("cancelled") || detail.toLowerCase().includes("transition"))) {
        const { saveOrderMapping, markAggregateDiscarded } = await import("@/lib/captainMesh/db");
        await saveOrderMapping(runningOrder.order_id, {
          order_id: serverId,
          order_status: "cancelled",
        });
        if (markAggregateDiscarded) await markAggregateDiscarded(runningOrder.order_id);
        setCancelPrompt(null);
        navigate(meshTablesPath());
        toast.success(`${table?.name || "Order"} cancelled`);
        return "done";
      }
      throw e;
    }
  };

  const send = async () => {
    if (!pending.length) return toast.error("Nothing new to send — add items first");
    // `pending` excludes queued lines on purpose: their intent already exists
    // and will reach the kitchen at recovery; a fresh intent would double it.
    const result = await fireKOT(pending);
    if (!result) return;

    // The intent is recorded; whether the kitchen has been told is a different
    // question, and only the server can answer it (bible §39.1). Claiming "KOT
    // sent" off the back of a local write is the exact failure the no-dead-UI
    // rule exists for — the captain walks away believing the food is on.
    let acknowledged = await kotIsServerAcknowledged(runningOrder.order_id);
    if (!acknowledged && (navigator.onLine ?? true)) {
      await new Promise(r => setTimeout(r, 400));
      acknowledged = await kotIsServerAcknowledged(runningOrder.order_id);
    }
    if (acknowledged) {
      toast.success(`KOT sent for ${table?.name}`);
    } else {
      toast.warning(`KOT queued for ${table?.name} — the kitchen has not been told yet`);
    }
    // Back to the floor (owner-set, 2026-08-20): a fired round means this
    // table is done for now — the captain's next move is another table, and
    // re-opening this one is a single tap from the grid.
    navigate(meshTablesPath());
  };

  return (
    <div className="flex flex-col h-full bg-background relative animate-slide-up" data-testid="captain-summary-screen">
      <header className="min-h-[4rem] pt-safe flex items-center justify-between px-3 bg-primary text-primary-foreground shadow-md sticky top-0 z-20 shrink-0">
        <div className="flex items-center gap-2">
          <button
            className="p-3 -ml-2 rounded-full hover:bg-primary-foreground/20 transition-colors"
            aria-label="Back to menu"
            data-testid="summary-back-button"
            onClick={back}
          >
            <ArrowLeft className="w-6 h-6"/>
          </button>
          <div className="flex flex-col">
            <small className="text-micro font-bold opacity-80 uppercase">
              {runningOrder ? `ORDER${runningOrder.order_number ? ` #${runningOrder.order_number}` : ""}` : 'SUMMARY'}
            </small>
            <h2 className="text-title font-bold leading-tight">{table?.name}</h2>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <NetworkIndicator />
          <button
            className="w-11 h-11 flex items-center justify-center rounded-full hover:bg-destructive text-primary-foreground transition-colors disabled:opacity-50"
            onClick={() => setCancelPrompt("manual")}
            disabled={orderBusy || !CANCELLABLE.has(runningOrder?.status) || !runningOrder?.server_order_id}
            title={runningOrder?.server_order_id ? "Cancel order" : "Not on the server yet"}
            aria-label="Cancel order"
          >
            <CircleSlash className="w-5 h-5"/>
          </button>
        </div>
      </header>

      {orderError && <div className="bg-destructive/10 text-destructive text-body font-semibold p-3 shrink-0">{orderError}</div>}

      <div className="flex-1 flex flex-col min-h-0 bg-muted/10">
        <div className="flex-1 overflow-y-auto p-base space-y-snug">
          {/* A walk-in gets no block at all — an empty "Guest: —" row is a line
              of chrome that says nothing the captain did not already know. */}
          {customer && (
            <div className="bg-card border rounded-xl shadow-sm p-base flex items-center gap-3" data-testid="summary-customer-block">
              <UserCircle className="w-5 h-5 text-muted-foreground shrink-0"/>
              <div className="flex flex-col min-w-0">
                <strong className="text-body font-bold text-foreground truncate">{customer.name || "Guest"}</strong>
                {customer.phone && <span className="text-meta text-muted-foreground truncate">{customer.phone}</span>}
              </div>
            </div>
          )}

          {pending.length ? (
            <>
              {pending.length > 0 && (
                <section
                  className="bg-card border border-warning/40 rounded-xl shadow-sm divide-y overflow-hidden"
                  data-testid="summary-unsent-section"
                >
                  <div className="px-4 py-3 bg-warning-subtle text-micro font-bold uppercase text-warning flex justify-between gap-3">
                    <span>New — not sent</span>
                    <span className="tabular-nums">{fmtQty(sumQty(pending.map(item => item.quantity)))} item(s)</span>
                  </div>
                  {/* kot_number is assigned by the server and by nothing else, so
                      it is the only thing entitled to say a line is sent — which
                      is why these lines carry no KOT number at all. The label
                      used to read `isOnline ? "Pending KOT" : ...`, reporting on
                      this device's connectivity rather than on whether the
                      kitchen had the ticket. */}
                  {pending.map(item => (
                    <SummaryLine
                      key={item.line_id}
                      item={item}
                      label="Pending KOT"
                      accentClass="text-warning"
                      onVoid={() => voidLine(item)}
                      onDelta={delta => updateQuantity(item.line_id, delta)}
                      onNote={() => setNoteFor(item)}
                      busy={orderBusy}
                    />
                  ))}
                </section>
              )}

              {/* New items only. Tax, service charge and the bill total are the
                  server's arithmetic (bible §39.1) and this device may be
                  offline — a total invented here is a figure the captain reads
                  aloud to a guest and the bill then contradicts. */}
              <div className="bg-card border rounded-xl shadow-sm p-base space-y-tight" data-testid="summary-subtotal">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-body font-semibold text-foreground">New items subtotal</span>
                  <span className={`${MONEY_LINE} text-foreground`}>{inr(subtotal)}</span>
                </div>
                <p className="text-micro text-muted-foreground">Taxes &amp; charges are computed at billing.</p>
              </div>
            </>
          ) : (
             // Claims only what THIS phone knows (bible §39.1, §47): a fired line may
             // still be a queued intent, not a KOT, and a table punched on another
             // device has no lines here at all — so neither "sent to the kitchen" nor
             // "this order is empty" is safe to say. The way to what the kitchen
             // actually holds is a visible button, not the long-press it used to name.
             <EmptyState
               title="Nothing new to send"
               message={lines.length
                 ? "Everything punched on this phone has been fired. View KOTs shows what the kitchen has."
                 : "Nothing is punched on this phone for this table. If it was punched on another device, View KOTs shows it."}
               action={
                 <button
                   type="button"
                   className="h-12 px-5 rounded-xl border bg-card text-foreground font-semibold text-body hover:bg-muted transition-colors"
                   onClick={() => navigate(meshKotsPath(table.id))}
                   data-testid="summary-view-kots-button"
                 >
                   View KOTs
                 </button>
               }
             />
          )}
        </div>
      </div>

      <footer className="p-4 bg-card border-t shadow-[0_-4px_12px_rgba(0,0,0,0.05)] sticky bottom-0 z-30 shrink-0" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom, 0px))" }}>
        {/* Nothing to fire here but the server holds unsent items: send them. */}
        <SendWaitingButton tableId={table?.id} show={!pending.length} className="w-full h-14 mb-2" />
        <button
          data-testid="fire-kot-button"
          className="w-full h-14 bg-action text-action-foreground rounded-xl font-bold uppercase text-body tracking-wider shadow-md hover:bg-action/90 transition-colors flex items-center justify-center disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none"
          onClick={send}
          disabled={orderBusy || !pending.length}
        >
          <ChefHat className="w-5 h-5 mr-2" />
          {pending.length ? `Fire KOT (${pending.length} new items)` : "Nothing to Fire"}
        </button>
      </footer>

      {noteFor && (
        <ItemNoteDialog
          key={noteFor.line_id}
          itemName={noteFor.item_name}
          initial={noteFor.notes || ""}
          presets={notePresets}
          onSave={saveNote}
          onClose={() => setNoteFor(null)}
        />
      )}

      {cancelPrompt && (
        <CaptainCancelDialog
          orderNumber={runningOrder?.order_number}
          tableName={table?.name}
          onConfirm={confirmCancelOrder}
          onClose={() => setCancelPrompt(null)}
        />
      )}
    </div>
  );
}
